/**
 * apps/web :: lib/onboarding-prefill
 *
 * Parses the marketing funnel-quiz hand-off query string into a typed
 * pre-fill record that the OnboardingWizard merges into its initial draft.
 *
 * Hand-off contract (ROADS-433):
 *   `${PUBLIC_WEB_APP_URL}/onboarding?state=CA&country=IN&hasLicense=yes&adult=yes`
 *
 * Backwards-compatible: any subset of the four query keys is accepted.
 * Unknown / malformed values are dropped silently so a stale or
 * tampered-with link can't crash the wizard — the visitor just lands on
 * the matching unanswered step.
 *
 * Status-agnostic (ROADS-436): the four query keys are state / country /
 * hasLicense / adult — none of which encode immigration status.
 */
import { US_STATES, type UsStateCode } from "./states";

/** Query-key contract. Marketing pins on these literals; do not rename. */
export const FUNNEL_PREFILL_KEYS = {
  state: "state",
  country: "country",
  hasLicense: "hasLicense",
  adult: "adult",
} as const;

export interface OnboardingPrefill {
  state: UsStateCode | null;
  country: string | null;
  hasLicense: "yes" | "no" | null;
  adult: "yes" | "no" | null;
}

const EMPTY_PREFILL: OnboardingPrefill = {
  state: null,
  country: null,
  hasLicense: null,
  adult: null,
};

const MVP_STATE_CODES: ReadonlySet<string> = new Set(
  US_STATES.map((s) => s.code),
);

function isMvpState(v: string | null): v is UsStateCode {
  return v !== null && MVP_STATE_CODES.has(v);
}

function isYesNo(v: string | null): v is "yes" | "no" {
  return v === "yes" || v === "no";
}

function isIsoCountry(v: string | null): v is string {
  return v !== null && /^[A-Z]{2}$/.test(v);
}

/**
 * Parse a `URLSearchParams`-shaped object into a normalized prefill. Each
 * field is independently validated; failures yield `null` for that field
 * only. Pure function — easy to unit-test without booting the wizard.
 */
export function parseOnboardingPrefill(
  params: URLSearchParams,
): OnboardingPrefill {
  const stateRaw = params.get(FUNNEL_PREFILL_KEYS.state);
  const stateUpper = stateRaw === null ? null : stateRaw.toUpperCase();
  const state: UsStateCode | null = isMvpState(stateUpper) ? stateUpper : null;

  const countryRaw = params.get(FUNNEL_PREFILL_KEYS.country);
  const countryUpper = countryRaw === null ? null : countryRaw.toUpperCase();
  const country: string | null = isIsoCountry(countryUpper)
    ? countryUpper
    : null;

  const hasLicenseRaw = params.get(FUNNEL_PREFILL_KEYS.hasLicense);
  const hasLicenseLower =
    hasLicenseRaw === null ? null : hasLicenseRaw.toLowerCase();
  const hasLicense: "yes" | "no" | null = isYesNo(hasLicenseLower)
    ? hasLicenseLower
    : null;

  const adultRaw = params.get(FUNNEL_PREFILL_KEYS.adult);
  const adultLower = adultRaw === null ? null : adultRaw.toLowerCase();
  const adult: "yes" | "no" | null = isYesNo(adultLower) ? adultLower : null;

  return { state, country, hasLicense, adult };
}

/**
 * Pull the prefill from the live `window.location.search`. SSR-safe — when
 * `window` is undefined returns the empty prefill. Errors silently degrade
 * to empty so a malformed search string never crashes the wizard.
 */
export function readOnboardingPrefillFromWindow(): OnboardingPrefill {
  if (typeof window === "undefined") return EMPTY_PREFILL;
  try {
    return parseOnboardingPrefill(new URLSearchParams(window.location.search));
  } catch {
    return EMPTY_PREFILL;
  }
}

export { EMPTY_PREFILL };
